import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import type {
  PayrollRosterFilterField,
  PayrollRosterFieldFilters,
  RosterFieldFilter,
} from "@/payroll/roster/types";
import {
  emptyRosterFilter,
  isRosterFilterActive,
  rosterFilterHasInput,
} from "@/payroll/roster/utils";
import { RosterFieldFilterChip } from "./roster-field-filter-chip";

const MAX_VISIBLE_FILTER_CHIPS = 6;

type OpenFilterPopover =
  | { kind: "field"; fieldKey: string }
  | { kind: "add" }
  | null;

interface RosterFilterControlsProps {
  filterFields: PayrollRosterFilterField[];
  leadingFilter?: ReactNode;
  filters: PayrollRosterFieldFilters;
  onChange: (filters: PayrollRosterFieldFilters) => void;
}

export const RosterFilterControls = ({
  filterFields,
  leadingFilter,
  filters,
  onChange,
}: RosterFilterControlsProps) => {
  const [openPopover, setOpenPopover] = useState<OpenFilterPopover>(null);
  const [revealedFieldKeys, setRevealedFieldKeys] = useState<string[]>([]);

  const isActive = ({ field }: PayrollRosterFilterField) => {
    const filter = filters[field.key];
    return filter != null && isRosterFilterActive(filter);
  };

  const visibleFilterFields = filterFields.filter(
    (filterField, index) =>
      index < MAX_VISIBLE_FILTER_CHIPS ||
      isActive(filterField) ||
      revealedFieldKeys.includes(filterField.field.key),
  );
  const visibleFieldKeys = new Set(
    visibleFilterFields.map(({ field }) => field.key),
  );
  const overflowFilterFields = filterFields.filter(
    ({ field }) => !visibleFieldKeys.has(field.key),
  );
  const activeCount = filterFields.filter(isActive).length;

  const setFilter = (fieldKey: string, next: RosterFieldFilter) => {
    const nextFilters = { ...filters };
    if (rosterFilterHasInput(next)) {
      nextFilters[fieldKey] = next;
    } else {
      delete nextFilters[fieldKey];
    }
    onChange(nextFilters);
  };

  const clearFilter = (fieldKey: string) => {
    const nextFilters = { ...filters };
    delete nextFilters[fieldKey];
    onChange(nextFilters);
  };

  if (filterFields.length === 0 && !leadingFilter) return null;

  return (
    <Stack
      direction="row"
      align="center"
      gap={2}
      className="min-w-0 flex-1 flex-wrap"
    >
      {leadingFilter}
      {visibleFilterFields.map(({ field, options, summary }) => (
        <RosterFieldFilterChip
          key={field.key}
          field={field}
          options={options}
          summary={summary}
          filter={filters[field.key] ?? emptyRosterFilter(field)}
          open={
            openPopover?.kind === "field" && openPopover.fieldKey === field.key
          }
          onOpenChange={(open) =>
            setOpenPopover(open ? { kind: "field", fieldKey: field.key } : null)
          }
          onChange={(next) => setFilter(field.key, next)}
          onClear={() => clearFilter(field.key)}
        />
      ))}

      {overflowFilterFields.length > 0 && (
        <Popover
          open={openPopover?.kind === "add"}
          onOpenChange={(open) => setOpenPopover(open ? { kind: "add" } : null)}
        >
          <PopoverTrigger
            render={<Button type="button" size="xs" variant="ghost" />}
          >
            <PlusIcon data-icon="inline-start" />
            {overflowFilterFields.length === 1
              ? "1 more field"
              : `${overflowFilterFields.length} more fields`}
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-0">
            <Command loop className="bg-transparent">
              <CommandInput placeholder="Find a field" />
              <CommandList className="max-h-64 overflow-y-auto overscroll-contain p-1">
                <CommandEmpty className="py-4 text-subtle">
                  No matching field
                </CommandEmpty>
                {overflowFilterFields.map(({ field }) => (
                  <CommandItem
                    key={field.key}
                    value={field.label}
                    onSelect={() => {
                      setRevealedFieldKeys((current) => [
                        ...current,
                        field.key,
                      ]);
                      setOpenPopover({ kind: "field", fieldKey: field.key });
                    }}
                  >
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={field.label}
                    >
                      {field.label}
                    </span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {activeCount > 1 && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          onClick={() => onChange({})}
        >
          Clear filters
        </Button>
      )}
    </Stack>
  );
};
